import { Box, Chip, Grid, TextField, List, InputAdornment, Popper, Avatar } from "@mzc-pdc/ui";
import React, {
    forwardRef,
    useCallback,
    useMemo,
    useRef,
    useState,
    ForwardedRef,
    useImperativeHandle,
    useEffect,
} from "react";
import { getProjectsAPI } from "@modules/projectManagement";
import { AutocompleteMultipleProjects } from "@components_v2/autocomplete";
import { useDispatch, useSelector } from "react-redux";

const ProjectsInput = ({
    data,
    isClear,
    project,
    disabled = false,
    onChange,
    placeholder,
    isEdit,
    required = false,
    error,
    helperText,
    sx,
    label,
    needFetch = true,
    customProjectList, //Note: needFetch가 false일 경우 사용(컴포넌트에서 다른 프로젝트 목록을 사용하려고 하는 경우 ex-일부 프로젝트만 표시 등)
    limitTags,
    isPopperComponent,
    noOptionsText,
    maxFields,
}) => {
    const space = useSelector(({ stage }) => stage);

    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState([]);
    const [selectedProject, setSelectedProject] = useState(data); //현재 선택한 유저(유저의 아이디만 popover로 넘겨주면 되서 자체력으로 autocomplete를 사용하도록 만들었습니다.)
    const [pendingProjects, setPendingProjects] = useState(false);
    const [currentSearchKeyword, setCurrentSearchKeyword] = useState(null);

    const projectPage = useRef(0);
    const totalProject = useRef(0);

    const LIMIT = 40;

    const loadProjectList = useCallback(
        async (isInitValue = false, searchKeyword = null) => {
            try {
                if (isInitValue) {
                    projectPage.current = 0;
                    totalProject.current = 0;
                }
                if (
                    pendingProjects ||
                    (totalProject.current > 0 && totalProject.current <= projectPage.current * LIMIT)
                )
                    return;
                setPendingProjects(true);
                const response = await getProjectsAPI(space.id, space.endpoint, {
                    offset: projectPage.current,
                    limit: LIMIT,
                    ...(searchKeyword && { keywordType: "name", keyword: searchKeyword }),
                });

                let newData = [];
                if (isInitValue) newData = response.data.results;
                else {
                    newData = [...options, ...response.data.results];
                }
                projectPage.current += 1;
                totalProject.current = response.data.totalCount;
                setOptions(newData);
            } catch (error) {
                console.error(error);
            } finally {
                setPendingProjects(false);
            }
        },
        [pendingProjects, space, data, options],
    );

    const onHandleDeleteUser = useCallback((index) => {
        setSelectedProject(null);
    }, []);

    const onHandleFetchUser = () => {
        if (needFetch) loadProjectList(false, currentSearchKeyword);
    };

    const handleInputChange = (reason) => {
        if (reason === "clear") {
            setSelectedProject(null);
        }
    };

    const handleClose = () => {
        setOpen(false);
    };

    const projectOptions = useMemo(() => {
        return (
            options?.map((project, idx) => ({
                name: project.name,
                id: project.id,
                lastOption: totalProject.current > options.length && options.length > 1 && idx === options.length - 1,
                ...(maxFields && {
                    disabled:
                        selectedProject?.length >= maxFields &&
                        !selectedProject?.find((selectedOption) => selectedOption.value === project.value),
                }),
            })) || []
        );
    }, [options, selectedProject]);

    const onChangeSearchKeyword = (name) => {
        setCurrentSearchKeyword(name);
        loadProjectList(true, name);
    };

    useEffect(() => {
        setSelectedProject(data);
    }, [data]);

    useEffect(() => {
        if (project) {
            setOptions([{ id: project.id, name: project.name }]);
        } else {
            if (needFetch) {
                loadProjectList(true);
            } else {
                setOptions(customProjectList);
            }
        }
    }, []);

    useEffect(() => {
        if (isClear) setSelectedProject(null);
    }, [isClear]);

    return (
        <AutocompleteMultipleProjects
            value={selectedProject ?? []}
            disabled={disabled}
            isEdit={isEdit}
            placeholder={placeholder}
            options={projectOptions}
            getOptionDisabled={(option) =>
                maxFields && selectedProject?.length >= maxFields && !selectedProject?.includes(option)
            }
            sx={sx}
            handleClose={handleClose}
            onFetchData={onHandleFetchUser}
            onInputChange={handleInputChange}
            onChange={onChange}
            onHandleDeleteUser={onHandleDeleteUser}
            onSubmit={() => {}}
            error={error}
            helperText={helperText}
            label={label}
            limitTags={limitTags}
            isPopperComponent={isPopperComponent}
            noOptionsText={noOptionsText}
            onChangeSearchKeyword={!project ? () => onChangeSearchKeyword() : null}
        />
    );
};

export default ProjectsInput;
